import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  Input,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  OnDestroy,
  EventEmitter,
  Output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';

import { PaymentOptionService } from '../../services/payment-option.service';
import { IconComponent } from '../icon/icon.component';
import { Icon } from '../../../core/models/icon.model';

export interface SlideButtonOption {
  label: string;
  value: string;
  icon?: Icon;
  imageUrl?: string;
}

@Component({
  selector: 'umkt-slide-button',
  standalone: true,
  imports: [CommonModule, MatIconModule, IconComponent],
  templateUrl: './slide-button.component.html',
  styleUrl: './slide-button.component.scss',
})
export class SlideButtonComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input({ required: true }) options: SlideButtonOption[] = [];
  @Input() defaultOptionValue: string = '';
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('indicator') indicator!: ElementRef<HTMLDivElement>;

  @ViewChildren('optionRef') optionsRef!: QueryList<ElementRef<HTMLDivElement>>;

  public slideButtonService = inject(PaymentOptionService);

  private isViewInitialized = false;

  private destroy$ = new Subject<void>();

  resizeHandler = () => this.updateIndicatorPosition();

  constructor() {
    effect(() => {
      this.slideButtonService.selected();
      this.valueChange.emit(this.slideButtonService.selected() as string);

      if (this.isViewInitialized) {
        setTimeout(() => this.updateIndicatorPosition(), 0);
      }
    });
  }

  ngOnInit(): void {
    const savedValue = this.slideButtonService.selected();

    if (!savedValue) {
      const hasDefaultValue = this.options.some(
        option => option.value === this.defaultOptionValue
      );

      let initialValue = '';
      if (hasDefaultValue) {
        initialValue = this.defaultOptionValue;
      } else if (this.options.length > 0) {
        initialValue = this.options[0].value;
      }

      if (initialValue) {
        this.slideButtonService.setSelected(initialValue);
      }
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    this.updateIndicatorPosition();
    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateIndicatorPosition(): void {
    if (!this.indicator || !this.optionsRef) return;

    const activeOption = this.optionsRef.find(
      optionEl =>
        optionEl.nativeElement.dataset['value'] ===
        this.slideButtonService.selected()
    );

    if (activeOption) {
      const optionElement = activeOption.nativeElement;
      const indicatorElement = this.indicator.nativeElement;

      const optionRect = optionElement.getBoundingClientRect();
      const parentRect =
        indicatorElement.parentElement!.getBoundingClientRect();

      const position = optionRect.left - parentRect.left;
      const width = optionRect.width;

      indicatorElement.style.transform = `translateX(${position}px)`;
      indicatorElement.style.width = `${width}px`;
    }
  }
}
